import { useEffect, useState } from "react";
import {
  Calendar,
  Plus,
  Save,
  Trash2,
  Trophy,
  Users,
  X,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import {
  fetchLeaderboardServers,
  fetchLeaderboardSeasons,
  fetchLeaderboardSeason,
  adminLeaderboardEvents,
} from "@/lib/api";

const EMPTY_EVENT = {
  name: "",
  slug: "",
  description: "",
  event_date: "",
  status: "draft",
  icon_url: "",
  max_points: "",
};

const EMPTY_POINT = {
  player_name: "",
  points: "",
  placement: "",
  note: "",
};

const STATUS_OPTIONS = [
  ["draft", "Draft"],
  ["active", "Active"],
  ["completed", "Completed"],
  ["archived", "Archived"],
];

export default function AdminLeaderboardEvents() {
  const [servers, setServers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [points, setPoints] = useState([]);

  const [eventPlayers, setEventPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [pointForm, setPointForm] = useState(EMPTY_POINT);

  const [editingEvent, setEditingEvent] = useState(null);
  const [editingPoint, setEditingPoint] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function initialLoad() {
      try {
        const data = await fetchLeaderboardServers();

        if (cancelled) return;

        setServers(data || []);

        if (data?.length && !selectedServer) {
          setSelectedServer(data[0].slug);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load leaderboard servers");
        }
      }
    }

    initialLoad();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedServer) {
      setSeasons([]);
      setSelectedSeason("");
      return;
    }

    loadSeasons(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (!selectedSeason) {
      setEvents([]);
      setSelectedEvent(null);
      setPoints([]);
      return;
    }

    let cancelled = false;

    async function refreshEvents() {
      try {
        setLoading(true);

        const data = await adminLeaderboardEvents.list(selectedSeason);

        if (cancelled) return;

        setEvents(data || []);

        if (selectedEvent) {
          const updated = (data || []).find(
            (event) => event.id === selectedEvent.id
          );

          if (updated) {
            setSelectedEvent(updated);

            const pointData =
              await adminLeaderboardEvents.points(updated.id);

            if (!cancelled) {
              setPoints(pointData || []);
            }
          } else {
            setSelectedEvent(null);
            setPoints([]);
          }
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load events");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    refreshEvents();

    return () => {
      cancelled = true;
    };
  }, [selectedSeason]);

  async function loadServers() {
    try {
      const data = await fetchLeaderboardServers();
      setServers(data || []);

      if (data?.length && !selectedServer) {
        setSelectedServer(data[0].slug);
      }
    } catch {
      toast.error("Failed to load leaderboard servers");
    }
  }

  async function loadSeasons(serverSlug) {
    try {
      const data = await fetchLeaderboardSeasons(serverSlug);
      setSeasons(data || []);

      if (data?.length) {
        const preferred =
          data.find((s) => s.status === "active") ||
          data.find((s) => s.status === "completed") ||
          data[0];

        setSelectedSeason(String(preferred.id));
      }
    } catch {
      toast.error("Failed to load seasons");
    }
  }

  async function loadEvents(seasonId) {
    setLoading(true);

    try {
      const data = await adminLeaderboardEvents.list(seasonId);
      setEvents(data || []);

      if (selectedEvent) {
        const updated = (data || []).find(
          (event) => event.id === selectedEvent.id
        );

        if (updated) {
          setSelectedEvent(updated);
          await loadPoints(updated.id);
        } else {
          setSelectedEvent(null);
          setPoints([]);
        }
      }
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }

  async function loadPoints(eventId) {
    try {
      const data = await adminLeaderboardEvents.points(eventId);

      const normalizedPoints = Array.isArray(data?.points)
        ? data.points
        : [];

      setPoints(normalizedPoints);

      await loadEventPlayers(
        eventId,
        normalizedPoints
      );
    } catch (err) {
      console.error("Failed to load event points:", err);
      toast.error("Failed to load event points");
    }
  }

  async function loadEventPlayers(eventId, eventPoints = []) {
    if (!selectedServer || !selectedSeason) {
      setEventPlayers([]);
      return;
    }

    try {
      const season = seasons.find(
        (item) => String(item.id) === String(selectedSeason)
      );

      if (!season?.slug) {
        setEventPlayers([]);
        return;
      }

      const data = await fetchLeaderboardSeason(
        selectedServer,
        season.slug
      );

      const leaderboardPlayers = Array.isArray(data?.entries)
        ? data.entries
        : Array.isArray(data?.players)
        ? data.players
        : Array.isArray(data)
        ? data
        : [];

      const existingPoints = Array.isArray(eventPoints)
        ? eventPoints
        : Array.isArray(eventPoints?.points)
        ? eventPoints.points
        : [];

      const existingMap = new Map(
        existingPoints.map((point) => [
          String(point.player_name || "").trim().toLowerCase(),
          point,
        ])
      );

      const merged = leaderboardPlayers
        .map((player, index) => {
          const playerName =
            player.name ||
            player.player_name ||
            player.username ||
            "";

          const existing = existingMap.get(
            playerName.trim().toLowerCase()
          );

          return {
            key:
              playerName.trim().toLowerCase() ||
              `player-${index}`,
            player_name: playerName,
            points:
              existing?.points != null
                ? String(existing.points)
                : "",
            placement:
              existing?.placement != null
                ? String(existing.placement)
                : "",
            note: existing?.note || "",
          };
        })
        .filter((player) => player.player_name);

      const known = new Set(
        merged.map((player) =>
          player.player_name.toLowerCase()
        )
      );

      existingPoints.forEach((point) => {
        const name = String(point.player_name || "").trim();

        if (!name || known.has(name.toLowerCase())) {
          return;
        }

        merged.push({
          key: name.toLowerCase(),
          player_name: name,
          points:
            point.points != null
              ? String(point.points)
              : "",
          placement:
            point.placement != null
              ? String(point.placement)
              : "",
          note: point.note || "",
        });
      });

      setEventPlayers(merged);
    } catch (err) {
      console.error("Failed to load event players:", err);
      setEventPlayers([]);
      toast.error("Failed to load season players");
    }
  }

  function updateEventPlayer(index, field, value) {
    setEventPlayers((current) =>
      current.map((player, i) =>
        i === index
          ? { ...player, [field]: value }
          : player
      )
    );
  }

  async function saveAllEventPoints() {
    if (!selectedEvent) return;

    setBulkSaving(true);

    try {
      const payload = eventPlayers
        .map((player) => ({
          player_name: player.player_name.trim(),
          points:
            player.points === ""
              ? 0
              : Number(player.points),
          placement:
            player.placement === ""
              ? null
              : Number(player.placement),
          note: (player.note || "").trim(),
        }))
        .filter(
          (player) =>
            player.player_name &&
            (
              player.points > 0 ||
              player.placement !== null ||
              player.note
            )
        );

      const invalid = payload.find(
        (player) =>
          !Number.isFinite(player.points) ||
          player.points < 0 ||
          (
            player.placement !== null &&
            (
              !Number.isFinite(player.placement) ||
              player.placement < 1
            )
          )
      );

      if (invalid) {
        toast.error(
          `Invalid points or placement for ${invalid.player_name}`
        );
        return;
      }

      console.log("SAVE ALL EVENT POINTS", {
        eventId: selectedEvent.id,
        payload,
        saucyZeno: payload.find(
          (p) => p.player_name.toLowerCase() === "saucyzeno"
        ),
      });

      await adminLeaderboardEvents.replacePoints(
        selectedEvent.id,
        payload
      );

      toast.success(
        `Saved ${payload.length} event player${payload.length === 1 ? "" : "s"}`
      );

      const pointData =
        await adminLeaderboardEvents.points(
          selectedEvent.id
        );

      const normalized =
        Array.isArray(pointData?.points)
          ? pointData.points
          : [];

      setPoints(normalized);

      await loadEventPlayers(
        selectedEvent.id,
        normalized
      );

      await loadEvents(selectedSeason);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Failed to save event points"
      );
    } finally {
      setBulkSaving(false);
    }
  }

  function startCreateEvent() {
    setEditingEvent(null);
    setEventForm(EMPTY_EVENT);
  }

  function startEditEvent(event) {
    setEditingEvent(event.id);

    setEventForm({
      name: event.name || "",
      slug: event.slug || "",
      description: event.description || "",
      event_date: event.event_date
        ? String(event.event_date).slice(0, 10)
        : "",
      status: event.status || "draft",
      icon_url: event.icon_url || "",
      max_points:
        event.max_points === null || event.max_points === undefined
          ? ""
          : String(event.max_points),
    });
  }

  async function saveEvent() {
    if (!selectedSeason) {
      toast.error("Select a season first");
      return;
    }

    if (!eventForm.name.trim()) {
      toast.error("Event name is required");
      return;
    }

    if (!eventForm.slug.trim()) {
      toast.error("Event slug is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        season_id: Number(selectedSeason),
        name: eventForm.name.trim(),
        slug: eventForm.slug.trim().toLowerCase(),
        description: eventForm.description.trim(),
        event_date: eventForm.event_date || null,
        status: eventForm.status,
        icon_url: eventForm.icon_url.trim() || null,
        max_points:
          eventForm.max_points === ""
            ? null
            : Number(eventForm.max_points),
      };

      if (editingEvent) {
        await adminLeaderboardEvents.update(editingEvent, payload);
        toast.success("Event updated");
      } else {
        await adminLeaderboardEvents.create(payload);
        toast.success("Event created");
      }

      setEventForm(EMPTY_EVENT);
      setEditingEvent(null);

      await loadEvents(selectedSeason);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to save event"
      );
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent(event) {
    if (!window.confirm(`Delete "${event.name}" and all its points?`)) {
      return;
    }

    try {
      await adminLeaderboardEvents.remove(event.id);

      if (selectedEvent?.id === event.id) {
        setSelectedEvent(null);
        setPoints([]);
      }

      toast.success("Event deleted");
      await loadEvents(selectedSeason);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to delete event"
      );
    }
  }

  async function selectEvent(event) {
    setSelectedEvent(event);
    setEditingPoint(null);
    setPointForm(EMPTY_POINT);
    await loadPoints(event.id);
  }

  async function savePoint() {
    if (!selectedEvent) {
      toast.error("Select an event first");
      return;
    }

    if (!pointForm.player_name.trim()) {
      toast.error("Player name is required");
      return;
    }

    if (
      pointForm.points === "" ||
      Number(pointForm.points) < 0
    ) {
      toast.error("Enter valid points");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        player_name: pointForm.player_name.trim(),
        points: Number(pointForm.points),
        placement:
          pointForm.placement === ""
            ? null
            : Number(pointForm.placement),
        note: pointForm.note.trim(),
      };

      if (editingPoint) {
        await adminLeaderboardEvents.updatePoint(
          selectedEvent.id,
          editingPoint,
          payload
        );
        toast.success("Event points updated");
      } else {
        await adminLeaderboardEvents.addPoint(
          selectedEvent.id,
          payload
        );
        toast.success("Event points added");
      }

      setPointForm(EMPTY_POINT);
      setEditingPoint(null);

      await loadPoints(selectedEvent.id);
      await loadEvents(selectedSeason);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Failed to save event points"
      );
    } finally {
      setSaving(false);
    }
  }

  function startEditPoint(point) {
    setEditingPoint(point.id);

    setPointForm({
      player_name: point.player_name || "",
      points:
        point.points === null || point.points === undefined
          ? ""
          : String(point.points),
      placement:
        point.placement === null ||
        point.placement === undefined
          ? ""
          : String(point.placement),
      note: point.note || "",
    });
  }

  async function deletePoint(point) {
    if (
      !window.confirm(
        `Remove ${point.player_name}'s ${point.points} points?`
      )
    ) {
      return;
    }

    try {
      await adminLeaderboardEvents.removePoint(
        selectedEvent.id,
        point.id
      );

      toast.success("Event points removed");

      await loadPoints(selectedEvent.id);
      await loadEvents(selectedSeason);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Failed to remove points"
      );
    }
  }

  return (
    <div className="space-y-6">

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Trophy size={20} />
            <h2 className="text-xl font-semibold">
              Leaderboard Events
            </h2>
          </div>

          <p className="mt-1 text-sm opacity-60">
            Create events and assign points to Minecraft players.
          </p>
        </div>

        <button
          onClick={() => selectedSeason && loadEvents(selectedSeason)}
          className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* Server / Season */}
      <div className="rounded-xl border p-4">
        <div className="grid gap-4 md:grid-cols-2">

          <div>
            <label className="mb-2 block text-xs font-medium opacity-70">
              Server
            </label>

            <select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
            >
              <option value="">Select server</option>

              {servers.map((server) => (
                <option key={server.id} value={server.slug}>
                  {server.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium opacity-70">
              Season
            </label>

            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
            >
              <option value="">Select season</option>

              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} — {season.status}
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_1.2fr]">

        {/* Events */}
        <div className="rounded-xl border p-5">

          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Events</h3>
              <p className="text-xs opacity-50">
                {events.length} event{events.length === 1 ? "" : "s"}
              </p>
            </div>

            <button
              onClick={startCreateEvent}
              disabled={!selectedSeason}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
            >
              <Plus size={15} />
              Create Event
            </button>
          </div>

          <div className="space-y-2">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => selectEvent(event)}
                className={`cursor-pointer rounded-lg border p-3 transition ${
                  selectedEvent?.id === event.id
                    ? "border-primary bg-primary/5"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3">

                  <div className="min-w-0">
                    <div className="flex items-center gap-2">

                      {event.icon_url ? (
                        <img
                          src={event.icon_url}
                          alt=""
                          className="h-7 w-7 rounded"
                        />
                      ) : (
                        <div className="flex h-7 w-7 items-center justify-center rounded bg-primary/10">
                          <Calendar size={14} />
                        </div>
                      )}

                      <div>
                        <div className="font-medium">
                          {event.name}
                        </div>

                        <div className="text-xs opacity-50">
                          {event.slug}
                        </div>
                      </div>

                    </div>

                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full border px-2 py-1">
                        {event.status}
                      </span>

                      {event.max_points !== null &&
                        event.max_points !== undefined && (
                          <span className="rounded-full border px-2 py-1">
                            Max {event.max_points} pts
                          </span>
                        )}

                      <span className="rounded-full border px-2 py-1">
                        {event.point_count || 0} players
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-1">

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditEvent(event);
                      }}
                      className="rounded p-2 hover:bg-muted"
                      title="Edit"
                    >
                      <Save size={14} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteEvent(event);
                      }}
                      className="rounded p-2 text-red-500 hover:bg-red-500/10"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>

                  </div>
                </div>
              </div>
            ))}

            {!events.length && !loading && (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <Calendar
                  size={28}
                  className="mx-auto mb-2 opacity-30"
                />

                <p className="text-sm opacity-60">
                  No events created for this season.
                </p>
              </div>
            )}
          </div>

          {/* Event form */}
          <div className="mt-6 border-t pt-5">

            <h4 className="mb-4 font-medium">
              {editingEvent ? "Edit Event" : "Create Event"}
            </h4>

            <div className="space-y-3">

              <input
                value={eventForm.name}
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    name: e.target.value,
                  })
                }
                placeholder="Event name"
                className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
              />

              <input
                value={eventForm.slug}
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    slug: e.target.value
                      .toLowerCase()
                      .replace(/\s+/g, "-"),
                  })
                }
                placeholder="event-slug"
                className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
              />

              <textarea
                value={eventForm.description}
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    description: e.target.value,
                  })
                }
                placeholder="Description"
                rows={3}
                className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
              />

              <div className="grid gap-3 sm:grid-cols-2">

                <input
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      event_date: e.target.value,
                    })
                  }
                  className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                />

                <input
                  type="number"
                  min="0"
                  value={eventForm.max_points}
                  onChange={(e) =>
                    setEventForm({
                      ...eventForm,
                      max_points: e.target.value,
                    })
                  }
                  placeholder="Maximum points"
                  className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                />

              </div>

              <select
                value={eventForm.status}
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    status: e.target.value,
                  })
                }
                className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              <input
                value={eventForm.icon_url}
                onChange={(e) =>
                  setEventForm({
                    ...eventForm,
                    icon_url: e.target.value,
                  })
                }
                placeholder="Icon URL (optional)"
                className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
              />

              <div className="flex gap-2">

                <button
                  onClick={saveEvent}
                  disabled={saving || !selectedSeason}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-40"
                >
                  <Save size={15} />
                  {saving
                    ? "Saving..."
                    : editingEvent
                    ? "Update Event"
                    : "Create Event"}
                </button>

                {editingEvent && (
                  <button
                    onClick={() => {
                      setEditingEvent(null);
                      setEventForm(EMPTY_EVENT);
                    }}
                    className="rounded-lg border px-4"
                  >
                    <X size={15} />
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Bulk Event Points */}
        <div className="rounded-xl border p-5">

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

            <div>
              <h3 className="font-semibold">
                Event Points
              </h3>

              <p className="text-xs opacity-50">
                {selectedEvent
                  ? `${selectedEvent.name} — ${eventPlayers.length} season players`
                  : "Select an event"}
              </p>
            </div>

            {selectedEvent && (
              <button
                onClick={() => loadPoints(selectedEvent.id)}
                disabled={bulkSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm disabled:opacity-40"
              >
                <RefreshCw size={14} />
                Refresh Players
              </button>
            )}

          </div>

          {!selectedEvent ? (

            <div className="rounded-lg border border-dashed p-10 text-center">

              <Users
                size={30}
                className="mx-auto mb-3 opacity-30"
              />

              <p className="text-sm opacity-60">
                Select an event to manage player points.
              </p>

            </div>

          ) : (

            <>

              <div className="mb-4 rounded-lg bg-muted/30 p-4">

                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">

                  <div>
                    <div className="font-medium">
                      Bulk Player Points
                    </div>

                    <div className="mt-1 text-xs opacity-50">
                      Players are loaded from the selected season.
                      Existing event points are pre-filled.
                    </div>
                  </div>

                  <button
                    onClick={saveAllEventPoints}
                    disabled={
                      bulkSaving ||
                      !eventPlayers.length
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-40"
                  >
                    <Save size={15} />

                    {bulkSaving
                      ? "Saving All..."
                      : "Save All Points"}
                  </button>

                </div>

                <div className="mt-4">

                  <input
                    value={playerSearch}
                    onChange={(e) =>
                      setPlayerSearch(e.target.value)
                    }
                    placeholder="Search player..."
                    className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm"
                  />

                </div>

              </div>

              <div className="overflow-x-auto rounded-lg border">

                <table className="w-full min-w-[760px] text-sm">

                  <thead>
                    <tr className="border-b bg-muted/30">

                      <th className="w-12 px-3 py-3 text-center">
                        #
                      </th>

                      <th className="px-3 py-3 text-left">
                        Player
                      </th>

                      <th className="w-32 px-3 py-3 text-left">
                        Points
                      </th>

                      <th className="w-32 px-3 py-3 text-left">
                        Placement
                      </th>

                      <th className="px-3 py-3 text-left">
                        Note
                      </th>

                    </tr>
                  </thead>

                  <tbody>

                    {eventPlayers
                      .map((player, originalIndex) => ({
                        player,
                        originalIndex,
                      }))
                      .filter(({ player }) =>
                        player.player_name
                          .toLowerCase()
                          .includes(
                            playerSearch
                              .trim()
                              .toLowerCase()
                          )
                      )
                      .map(
                        (
                          { player, originalIndex },
                          visibleIndex
                        ) => (

                          <tr
                            key={player.key}
                            className="border-b last:border-0 hover:bg-muted/20"
                          >

                            <td className="px-3 py-2 text-center text-xs opacity-50">
                              {visibleIndex + 1}
                            </td>

                            <td className="px-3 py-2 font-medium">
                              {player.player_name}
                            </td>

                            <td className="px-3 py-2">

                              <input
                                type="number"
                                min="0"
                                value={player.points}
                                onChange={(e) =>
                                  updateEventPlayer(
                                    originalIndex,
                                    "points",
                                    e.target.value
                                  )
                                }
                                className="w-full rounded-md border bg-transparent px-2 py-1.5 text-sm"
                              />

                            </td>

                            <td className="px-3 py-2">

                              <input
                                type="number"
                                min="1"
                                value={player.placement}
                                onChange={(e) =>
                                  updateEventPlayer(
                                    originalIndex,
                                    "placement",
                                    e.target.value
                                  )
                                }
                                placeholder="—"
                                className="w-full rounded-md border bg-transparent px-2 py-1.5 text-sm"
                              />

                            </td>

                            <td className="px-3 py-2">

                              <input
                                value={player.note}
                                onChange={(e) =>
                                  updateEventPlayer(
                                    originalIndex,
                                    "note",
                                    e.target.value
                                  )
                                }
                                placeholder="Optional note"
                                className="w-full min-w-[180px] rounded-md border bg-transparent px-2 py-1.5 text-sm"
                              />

                            </td>

                          </tr>

                        )
                      )}

                    {!eventPlayers.length && (

                      <tr>
                        <td
                          colSpan="5"
                          className="px-4 py-10 text-center opacity-50"
                        >
                          No players found for this season.
                        </td>
                      </tr>

                    )}

                    {eventPlayers.length > 0 &&
                      !eventPlayers.some((player) =>
                        player.player_name
                          .toLowerCase()
                          .includes(
                            playerSearch
                              .trim()
                              .toLowerCase()
                          )
                      ) && (

                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-10 text-center opacity-50"
                          >
                            No players match your search.
                          </td>
                        </tr>

                      )}

                  </tbody>

                </table>

              </div>

              <div className="mt-3 flex flex-col gap-1 text-xs opacity-50 sm:flex-row sm:items-center sm:justify-between">

                <span>
                  Showing{" "}
                  {eventPlayers.filter((player) =>
                    player.player_name
                      .toLowerCase()
                      .includes(
                        playerSearch
                          .trim()
                          .toLowerCase()
                      )
                  ).length}{" "}
                  of {eventPlayers.length} players
                </span>

                <span>
                  Players with 0 points are not stored as event results.
                </span>

              </div>

            </>

          )}

        </div>

      </div>
    </div>
  );
}
